import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { t, type StringKey } from './i18n'
import type { ModerationAction } from './moderation'
import type { ShareAnswer, SharePending } from './useClassroomSignals'

/**
 * The teacher's per-student controls, on the student's own tile.
 *
 * On the tile rather than in a roster panel because that is where the teacher is
 * already looking: they can see who is talking over the lesson, and the control
 * for it is on that face.
 *
 * The sheet is position: FIXED and placed from the trigger's measured rect,
 * because a tile must clip its own contents to keep its rounded corners — an
 * absolutely positioned popover inside one gets cut in half, which is exactly
 * what happened first time round. Placement is clamped to the viewport on BOTH
 * axes — X when the sheet is placed, Y after it is measured, because its height
 * depends on which controls a given student earns — and on a phone it becomes a
 * bottom sheet instead: 232px of menu does not belong inside a 180px tile.
 *
 * The asymmetry in this menu is deliberate and worth keeping. "Mute" and "camera
 * off" take effect immediately and cannot be refused — the teacher needs to be
 * able to settle a class. "Ask to unmute" and "ask for camera" only send a
 * request the student accepts, because our students are children at home and an
 * adult silently opening their camera is not something this app should be able
 * to do. The labels say which is which, so a teacher is never surprised.
 */

interface Props {
  name: string
  micMuted: boolean
  cameraOff: boolean
  sharing: boolean
  onModerate: (action: ModerationAction) => Promise<void>
  onAsk: (source: 'microphone' | 'camera') => void
  /** Ask THIS student to show their screen (teacher-only, undefined = hidden). */
  onAskShare?: () => void
  /**
   * A parent's token cannot publish a screen share, so "ask to share" would
   * dead-end — the request would go out and could never be accepted. Suppressed
   * here rather than at every call site so the rule lives with the control.
   */
  isParent?: boolean
  /** Their device advertised it can capture a screen. Missing means NO. */
  canShare: boolean
  /**
   * Why their microphone is unavailable, if it is. "Ask to unmute" is the WRONG
   * offer when the browser is withholding the device — the child taps nothing,
   * the tutor reads it as being ignored, and on 2026-08-02 that misreading burned
   * nineteen minutes of a paid lesson before the class fled to Zoom. Same lesson
   * as canShare: replace the control with the reason rather than leaving a live
   * button that cannot work.
   */
  micReason?: string | null
  /** Their answer to the last ask, so the teacher is not left guessing. */
  sharePending?: SharePending
  /** Someone is already presenting — one share at a time. */
  shareBusy: boolean
  /**
   * Asks to remove this student (the caller confirms first). Undefined hides
   * the item: only students can be removed, never a parent or staff.
   */
  onRemove?: () => void
}

/** Keep in step with .tile-menu__sheet's min-width. */
const SHEET_WIDTH = 232
const EDGE_GAP = 8

export function TileMenu({
  name,
  micMuted,
  cameraOff,
  sharing,
  onModerate,
  onAsk,
  onAskShare,
  isParent,
  canShare,
  micReason,
  sharePending,
  shareBusy,
  onRemove,
}: Props) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  // The server's own message, not a generic one. "That participant is not in this
  // class" tells a teacher (and whoever they report to) something; "that did not
  // work" told nobody anything, and cost a production bisect to diagnose.
  const [failed, setFailed] = useState<string | null>(null)
  const [asked, setAsked] = useState<'microphone' | 'camera' | null>(null)
  const [askedShare, setAskedShare] = useState(false)
  const [at, setAt] = useState<
    { top: number; left: number; triggerTop: number } | null
  >(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  // The "asked" confirmation clears itself after 1.4s; tracked so leaving the
  // room (or the tile disappearing) mid-countdown does not fire setState against
  // an unmounted component.
  const askTimerRef = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(askTimerRef.current), [])

  // The real answer supersedes the local "asked" flash.
  useEffect(() => {
    if (sharePending) setAskedShare(false)
  }, [sharePending])

  // Right-aligned to the trigger, then clamped into the viewport. Clamping rather
  // than direction-aware CSS keeps this correct in both LTR and RTL without two
  // code paths.
  const toggle = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect) {
      // max() LAST. The old order — min(max(gap, x), width - sheet - gap) — puts
      // the upper bound last, and on a viewport narrower than the sheet plus its
      // gaps that bound is itself below the gap, so the "clamp" returned a
      // NEGATIVE left and pushed the sheet off the edge it was meant to protect.
      const left = Math.max(
        EDGE_GAP,
        Math.min(rect.right - SHEET_WIDTH, window.innerWidth - SHEET_WIDTH - EDGE_GAP),
      )
      setAt({ top: rect.bottom + 6, left, triggerTop: rect.top })
    }
    setOpen((v) => !v)
  }

  /**
   * The vertical half of that clamp, which never existed — the comment at the
   * top of this file promised the sheet "can never open off-screen" while only X
   * was ever bounded. A tile in the lower half of the stage opened a menu that
   * ran under the control bar and off the bottom of the window, with its last
   * items unreachable.
   *
   * It has to be measured rather than computed: this menu's height depends on
   * which controls a given student earns (a blocked-mic note, ask-to-share, the
   * error row), so there is no constant to check against. Below the trigger is
   * preferred, above is the flip when the space is not there, and if neither
   * side fits the sheet takes what is left and scrolls — a short window must
   * still be able to reach "Mute".
   */
  useLayoutEffect(() => {
    if (!open || !at) return
    const sheet = sheetRef.current
    if (!sheet) return
    // The phone layout is a bottom sheet pinned by CSS !important; measuring and
    // re-placing it here would fight that and lose.
    if (window.matchMedia('(max-width: 700px)').matches) return

    const height = sheet.getBoundingClientRect().height
    const limit = window.innerHeight - EDGE_GAP
    if (at.top + height <= limit) return

    const above = at.triggerTop - 6 - height
    const next = above >= EDGE_GAP ? above : Math.max(EDGE_GAP, limit - height)
    if (Math.abs(next - at.top) < 1) return
    setAt({ ...at, top: next })
  }, [open, at])

  // A rotated phone or a resized window leaves the sheet pointing at nothing.
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('resize', close)
    window.addEventListener('orientationchange', close)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('orientationchange', close)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Swallowed here so it reaches nothing else. This menu is now also
        // rendered inside the participants panel, which closes itself on Escape
        // from its own document listener — so one Escape closed the menu AND tore
        // down the panel around it. The innermost thing should win.
        //
        // Registered in the CAPTURE phase for that to actually hold: same-target
        // listeners fire in registration order, and the panel mounts its listener
        // before this one, so a bubble-phase stopImmediatePropagation here would
        // run too late to stop it. Capture on document runs before any bubble
        // listener on document.
        event.stopImmediatePropagation()
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  const run = async (action: ModerationAction) => {
    setBusy(true)
    setFailed(null)
    try {
      await onModerate(action)
      setOpen(false)
    } catch (error) {
      // Kept open with the failure visible. Silently closing would leave the
      // teacher believing a disruptive student had been muted.
      setFailed((error as Error)?.message || t('moderate.failed'))
    } finally {
      setBusy(false)
    }
  }

  // Confirmed, then closed. A request that vanishes with no acknowledgement is
  // indistinguishable from a broken button — which is how this read on first use.
  const ask = (source: 'microphone' | 'camera') => {
    onAsk(source)
    setAsked(source)
    window.clearTimeout(askTimerRef.current)
    askTimerRef.current = window.setTimeout(() => {
      setAsked(null)
      setOpen(false)
    }, 1400)
  }

  return (
    <div className="tile-menu" ref={wrapRef}>
      <button
        type="button"
        className="tile-menu__trigger"
        ref={buttonRef}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('moderate.for').replace('{name}', name)}
        title={t('moderate.for').replace('{name}', name)}
      >
        <DotsIcon />
      </button>

      {open ? (
        <div
          ref={sheetRef}
          className="tile-menu__sheet"
          role="menu"
          aria-busy={busy || undefined}
          style={at ? { top: `${at.top}px`, left: `${at.left}px` } : undefined}
        >
          <p className="tile-menu__title">
            <bdi dir="auto">{name}</bdi>
          </p>

          {micReason ? (
            <div className="tile-menu__note tile-menu__note--blocked" role="note">
              <p className="tile-menu__note-title">{t('tile.micBlockedLabel')}</p>
              <p className="tile-menu__note-body">{micReason}</p>
              <p className="tile-menu__note-body">{t('tile.micHelpHint')}</p>
            </div>
          ) : micMuted ? (
            <MenuItem onClick={() => ask('microphone')} icon="🎤">
              {t('moderate.askMic')}
            </MenuItem>
          ) : (
            <MenuItem onClick={() => run('mute_audio')} disabled={busy} icon="🔇">
              {t('moderate.muteMic')}
            </MenuItem>
          )}

          {cameraOff ? (
            <MenuItem onClick={() => ask('camera')} icon="📷">
              {t('moderate.askCamera')}
            </MenuItem>
          ) : (
            <MenuItem onClick={() => run('mute_video')} disabled={busy} icon="🚫">
              {t('moderate.cameraOff')}
            </MenuItem>
          )}

          {sharing ? (
            <MenuItem onClick={() => run('stop_share')} disabled={busy} icon="🛑">
              {t('moderate.stopShare')}
            </MenuItem>
          ) : onAskShare && !isParent ? (
            /* Disabled-with-a-reason rather than hidden: a teacher who cannot
               find the control assumes the app lacks it, and "this phone
               cannot share a screen" is the single most useful thing to know
               about a class of children on Android. */
            <MenuItem
              onClick={() => {
                onAskShare()
                setAskedShare(true)
                window.clearTimeout(askTimerRef.current)
                askTimerRef.current = window.setTimeout(() => setOpen(false), 1400)
              }}
              disabled={!canShare || shareBusy || sharePending?.state === 'waiting'}
              icon="🖥️"
            >
              {!canShare
                ? t('moderate.shareUnavailable')
                : shareBusy
                  ? t('moderate.shareBusy')
                  : sharePending?.state === 'waiting'
                    ? t('moderate.shareWaiting')
                    : t('moderate.askShare')}
            </MenuItem>
          ) : null}

          {onRemove ? (
            <MenuItem
              onClick={() => {
                // Focus goes back to the trigger before the dialog opens, so
                // cancelling it returns a keyboard user to this tile.
                setOpen(false)
                buttonRef.current?.focus()
                onRemove()
              }}
              disabled={busy}
              icon="🚪"
              danger
            >
              {t('moderate.remove')}
            </MenuItem>
          ) : null}

          {sharePending?.state === 'answered' ? (
            <p
              className={
                sharePending.answer === 'accepted' ? 'tile-menu__ok' : 'tile-menu__note'
              }
              role="status"
            >
              {sharePending.answer === 'accepted' ? '✓ ' : ''}
              {t(shareAnswerKey(sharePending.answer)).replace('{name}', name)}
            </p>
          ) : askedShare ? (
            <p className="tile-menu__ok" role="status">
              ✓ {t('moderate.askedShare')}
            </p>
          ) : asked ? (
            <p className="tile-menu__ok" role="status">
              ✓ {t(asked === 'microphone' ? 'moderate.askedMic' : 'moderate.askedCamera')}
            </p>
          ) : (
            /* Says out loud that the two halves of this menu behave differently. */
            <p className="tile-menu__note">{t('moderate.askNote')}</p>
          )}
          {failed ? (
            <p className="tile-menu__error" role="alert">
              {failed}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function MenuItem({
  onClick,
  children,
  icon,
  disabled,
  danger,
}: {
  onClick: () => void
  children: React.ReactNode
  icon: string
  disabled?: boolean
  danger?: boolean
}) {
  return (
    <button
      type="button"
      className={`tile-menu__item ${danger ? 'tile-menu__item--danger' : ''}`}
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
    >
      {/* Wrapped rather than bare text nodes: a bare child becomes an anonymous
          flex item that cannot be given min-width, so a long label overflowed
          the sheet instead of wrapping inside it. */}
      <span className="tile-menu__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="tile-menu__label">{children}</span>
    </button>
  )
}

const DotsIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
)

/** Tutor-facing wording for each answer. Declines stay soft and private. */
function shareAnswerKey(answer: ShareAnswer): StringKey {
  switch (answer) {
    case 'accepted':
      return 'moderate.shareAccepted'
    case 'declined':
      return 'moderate.shareDeclined'
    case 'timeout':
      return 'moderate.shareNoAnswer'
    case 'busy':
      return 'moderate.shareBusy'
    case 'unsupported':
      return 'moderate.shareUnavailable'
    case 'cancelled':
      return 'moderate.shareCancelled'
    default:
      return 'moderate.shareFailed'
  }
}
